import { Icon, Box, sanitizeToNumeric } from 'vopay';
import { useHistory } from 'react-router-dom';
import ElinxForm from './ElinxForm';
import { ElinxInfoSerialized } from './types';
import usePostSendPaylink from '@/hooks/useApi/usePostSendPaylink';
import usePostEmbedUrl from '@/hooks/useApi/usePostEmbedUrl';
import usePaymentMethodCurrencySupport from '@/hooks/useFields/TransactionCreationFields/usePaymentMethodCurrencySupport';
import useUploadDocument from '@/hooks/useApi/useUploadDocument';
import useFundMyAccount from '@/hooks/useApi/useFundMyAccount';
import { useDataFetchIndicator } from '@/hooks';
import { useConfirmDialog } from '@/providers/ConfirmDialog.provider';
import { Title } from 'UI';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/providers/Toast.provider';
import useContact from './useContact';
import { DuplicateElinxRequestDialog } from './DuplicateElinxRequestDialog';

const ElinxCollectContainer: React.FC = () => {
  const history = useHistory();
  const toast = useToast();
  const [, , , sendElinx] = usePostSendPaylink({});
  const [, , , postEmbedUrl] = usePostEmbedUrl({});
  const { isDomesticCurrency } = usePaymentMethodCurrencySupport('Pay');
  const { fundMyAccount } = useFundMyAccount();
  const [, setDataFetchingInfo] = useDataFetchIndicator();
  const confirmDialog = useConfirmDialog();
  const { t } = useTranslation('translations', { keyPrefix: 'Elinx.EPay' });
  const { t: tElinx } = useTranslation('translations', { keyPrefix: 'Elinx' });
  const { uploadDocument } = useUploadDocument();
  const { getContact, saveContactEmail } = useContact();
  const { showIfDuplicateRequest } = DuplicateElinxRequestDialog();

  const onSubmit = async (values: ElinxInfoSerialized) => {
    try {
      setDataFetchingInfo(true, 'loading some data', 'fullScreen');

      const { InvoiceFile, InvoiceName, AdditionalRecipientEmails, PayFrom, ...rest } = values;

      // Pay From ('never' trigger only — see useElinxBasicDetailsRows): fund the account from the
      // selected source first, then hand the resulting transaction to /elinx as its funding parent.
      // Every other trigger funds automatically server-side, so no ParentTransactionID is sent.
      let fundingTransactionID: string | undefined;

      // Recurrence is UI-only and never serialized — Frequency is the scheduled/not-scheduled
      // signal that actually reaches this submitted payload (undefined for a one-time request).
      if (PayFrom && PayFrom !== 'balance' && !values.Frequency) {
        try {
          const fundResponse = await fundMyAccount({
            Token: '',
            Amount: values.Amount ? Number(sanitizeToNumeric(values.Amount)) : 0,
            Currency: values.Currency,
            Source: PayFrom,
          });

          fundingTransactionID = fundResponse.TransactionID;
        } catch {
          const fundingErrorMessage = t('FundingError.prefix', {
            defaultValue: "We couldn't fund your account from the selected source",
          });

          toast.neutral(fundingErrorMessage);

          // Abort here instead of falling through to create an active, unfunded ePay request.
          return;
        }
      }

      // earthnode (ID-13659) rejects an IframeKey on a request in the account's other currency — the
      // iframe row's methods are shared by every eLinx made with it, and the currency must narrow
      // them — and builds the hosted page itself instead. The Card Settings' brands still reach it
      // through the request body; the method and account-selection choices do not.
      let iframeKey: string | undefined;

      if (isDomesticCurrency(values.Currency)) {
        const embedUrlResponse = await postEmbedUrl({
          ...rest,
          RedirectURL: '',
          RedirectMethod: 'JavascriptMessage',
          ClientControlled: false,
          Language: 'en',
          ClientAccountID: values?.Contact,
          AcceptedCardBrands: values.AcceptedCardBrands,
          CardTypeValidation: values.CardTypeValidation,
        });

        if (!embedUrlResponse.Success) {
          throw new Error(embedUrlResponse.ErrorMessage);
        }

        iframeKey = embedUrlResponse.IframeKey;
      }

      const emailAddress = values.OverrideEmailAddress || values.NewEmailAddress;
      const receiverEmailAddress = [...new Set([emailAddress, ...AdditionalRecipientEmails].filter(Boolean))].join(',');

      const hasInvoice = InvoiceFile !== '' && InvoiceName !== '';

      const documentID = hasInvoice
        ? (
            await uploadDocument({
              DocumentContent: InvoiceFile || '',
              DocumentName: InvoiceName || '',
              DocumentType: 'invoice',
            })
          ).DocumentID
        : '';

      const elinxResponse = await sendElinx({
        ...rest,
        IframeKey: iframeKey,
        ReceiverEmailAddress: receiverEmailAddress,
        ReceiverName:
          values?.ExistingContactName ||
          (values.ContactType === 'business'
            ? values.BusinessName
            : `${values.FirstName || ''} ${values.LastName || ''}`.trim()) ||
          '',
        Amount: values.Amount ? Number(sanitizeToNumeric(values.Amount)) : undefined,
        PaymentType: 'withdraw',
        ClientAccountID: values?.Contact,
        ReceiverPhoneNumber: values?.ReceiverPhoneNumber?.replace('+', ''),
        BankingPaymentRail: values.PaymentSelectionMethod?.includes('bank') ? values.BankingPaymentRail : undefined,
        DocumentID: documentID,
        ParentTransactionID: fundingTransactionID,
      });

      if (!elinxResponse.Success) {
        throw new Error(elinxResponse.ErrorMessage);
      }

      const contactResponse = await getContact({
        ContactID: values.Contact,
      });

      if (contactResponse.EmailAddress === '' && values.OverrideEmailAddress) {
        try {
          await saveContactEmail({
            ContactID: values.Contact,
            EmailAddress: values.OverrideEmailAddress,
          });
        } catch {
          toast.neutral(tElinx('savingEmailError', { defaultValue: 'Failed to save the email address' }));
        }
      }

      const sendNotificationMethod = values?.SendNotification ? values?.NotificationMethod : 'none';
      const isScheduled = !!values?.ScheduleStartDate;

      const queryParams = new URLSearchParams({
        scheduled: isScheduled.toString(),
      });

      history.push(
        `/elinx-history/pay/confirmation/${elinxResponse.ELinxRequestID}/${sendNotificationMethod}/none?${queryParams.toString()}`,
      );
    } catch (err) {
      console.error(err);
      if (showIfDuplicateRequest(err)) return;

      const subTitle = err instanceof Error ? err.message : undefined;

      confirmDialog.error(t('SendPaylinkError.title'), {
        subTitle,
        cancelLabel: t('SendPaylinkError.cancelLabel'),
      });
    } finally {
      setDataFetchingInfo(false);
    }
  };

  return (
    <Box flx="row" jc="center">
      <Box flx="col" jc="center" maxwidth="1121px" boxWidth="100%" p="md">
        <Box flx="col" gapSize="32px">
          <Title>
            <Box ai="baseline" gapSize="4px">
              <Icon icon="eLinxE" height="18px" width="18px" />
              {t('title')}
            </Box>
          </Title>

          <div>{tElinx('Cards.EPay.description')}</div>
        </Box>

        <Box base="8" pt="xmd">
          <ElinxForm onSubmit={onSubmit} formType="Pay" />
        </Box>
      </Box>
    </Box>
  );
};

export default ElinxCollectContainer;
